import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { api } from "@/lib/api";
import ProtectedRoute from "@/components/ProtectedRoute";
import MembershipGate from "@/components/MembershipGate";
import Landing from "@/pages/Landing";
import Plans from "@/pages/Plans";
import Vault from "@/pages/Vault";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Assessment from "@/pages/Assessment";
import Dashboard from "@/pages/Dashboard";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import Passport from "@/pages/Passport";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Refunds from "@/pages/Refunds";
import Cookies from "@/pages/Cookies";
import Aup from "@/pages/Aup";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import Exam from "@/pages/Exam";
import RoyAI from "@/pages/RoyAI";
import InstallPrompt from "@/components/InstallPrompt";
import { Toaster } from "sonner";

function AppRouter() {
  const location = useLocation();

  // Anonymous pageview beacon — fires on every route change so we count
  // visitors landing on public pages (`/`, `/c/:username`, `/terms`) that
  // don't otherwise call any data endpoint. Fire-and-forget; ignore errors.
  useEffect(() => {
    api.post("/telemetry/pageview", { path: location.pathname }).catch(() => {});
  }, [location.pathname]);

  // Process Emergent OAuth callback before any other routing
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/plans" element={<Plans />} />
      <Route path="/vault" element={<ProtectedRoute><MembershipGate label="The Verified Evidence Vault" allow={["master", "royai_elite", "passport_pro"]}><Vault /></MembershipGate></ProtectedRoute>} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refunds" element={<Refunds />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/aup" element={<Aup />} />
      <Route path="/c/:username" element={<Passport />} />
      <Route path="/assessment" element={<ProtectedRoute><Assessment /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><MembershipGate label="The Dashboard" allow={["master", "royai_elite", "passport_pro", "vip_trial"]}><Dashboard /></MembershipGate></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
      <Route path="/courses" element={<ProtectedRoute><MembershipGate label="The Learning Roadmap"><Courses /></MembershipGate></ProtectedRoute>} />
      <Route path="/courses/:slug" element={<ProtectedRoute><MembershipGate label="This course"><CourseDetail /></MembershipGate></ProtectedRoute>} />
      <Route path="/courses/:slug/exam" element={<ProtectedRoute><MembershipGate label="This exam"><Exam /></MembershipGate></ProtectedRoute>} />
      <Route path="/royai" element={<ProtectedRoute><RoyAI /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
      <Route path="*" element={<Landing />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <InstallPrompt />
          <Toaster theme="dark" position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
